import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
} from 'react';
import {
  formatEquation,
  substitute,
  type AlgebraOperation,
} from '../shared/algebra.ts';
import { teachingBeats, TEACHING_VERSION } from '../shared/teaching.ts';
import type {
  ChoiceOption,
  LearningSession,
  LessonContent,
  SessionAction,
  StageId,
} from '../shared/types.ts';
import { Icon } from './components/Icon.tsx';
import { AnimatedBoard, useReducedMotion } from './components/AnimatedBoard.tsx';
import { EquationWorkshop } from './components/EquationWorkshop.tsx';
import { OperationDemonstration } from './components/OperationDemonstration.tsx';
import { lessonFrame, replyFocus, type SceneFrame } from './lib/lessonScene.ts';
import { narratedOperation } from './lib/narratedOperation.ts';
import { detectSpokenMath, parseLinearEquationDraft } from '../shared/spokenMath.ts';
import { TutorAvatar } from './components/TutorAvatar.tsx';
import { LearningRecord } from './components/LearningRecord.tsx';
import type { AudioManifest, Exposure } from './components/NarrationPlayer.tsx';
import { api, ApiError } from './lib/api.ts';
import { commandId } from './lib/commandId.ts';
import { interpretOperation } from './lib/interpret.ts';
import { useRecorder } from './lib/useRecorder.ts';
import { useTutorVoice } from './lib/useTutorVoice.ts';

const stages: StageId[] = ['concept', 'application', 'transfer'];
const names = {
  concept: 'Understand',
  application: 'Apply',
  transfer: 'Try a new situation',
};
const SESSION_KEY = 'enactry.internal.session.v1';
const hasModel = (p: LearningSession['stages'][StageId]) => !!(p.unknownAnswer?.correct && (p.modelAnswer?.correct || p.modelEquation?.correct));
function parsedDraft(text: string) { try { return parseLinearEquationDraft(text); } catch { return undefined; } }
type Mode = 'welcome' | 'teach' | 'practice';
type Panel = 'model' | 'solve' | 'check';
type Tray = 'ask' | 'transcript' | 'history' | 'settings';
type Boot = {
  lesson: LessonContent;
  session: LearningSession;
  manifest?: AudioManifest;
  capabilities?: { tts: { available: boolean }; stt: { available: boolean } };
};
async function bootstrap(): Promise<Boot> {
  const lesson = await api<LessonContent>('/api/lesson');
  let session: LearningSession | undefined;
  const saved = localStorage.getItem(SESSION_KEY);
  if (saved) {
    try {
      session = await api<LearningSession>(`/api/sessions/${saved}`);
    } catch (err) {
      if (!(err instanceof ApiError && err.status === 404)) throw err;
    }
  }
  session ??= await api<LearningSession>('/api/sessions', {
    method: 'POST',
    body: '{}',
  });
  localStorage.setItem(SESSION_KEY, session.id);
  const [manifest, capabilities] = await Promise.all([
    fetch('/audio/manifest.json')
      .then((r) => (r.ok ? (r.json() as Promise<AudioManifest>) : undefined))
      .catch(() => undefined),
    api<Boot['capabilities']>('/api/capabilities').catch(() => undefined),
  ]);
  return { lesson, session, manifest, capabilities };
}
function Choices({
  options,
  selected,
  onChoose,
  disabled,
}: {
  options: ChoiceOption[];
  selected?: string;
  onChoose: (id: string) => void;
  disabled: boolean;
}) {
  return (
    <div className="choices">
      {options.map((o, i) => (
        <button
          key={o.id}
          className={`choice ${selected === o.id ? 'selected' : ''}`}
          aria-pressed={selected === o.id}
          disabled={disabled}
          onClick={() => onChoose(o.id)}
        >
          <span className="choice-letter">{String.fromCharCode(65 + i)}</span>
          <span>{o.label}</span>
          {selected === o.id && <Icon name="check" />}
        </button>
      ))}
    </div>
  );
}
const clock = (ms: number) =>
  `${Math.floor(ms / 60000)}:${String(Math.floor(ms / 1000) % 60).padStart(2, '0')}`;
function substitutionText(
  equation: Parameters<typeof substitute>[0],
  value: string,
) {
  const result = substitute(equation, value);
  return `${result.left} ${result.equal ? '=' : '≠'} ${result.right}`;
}

export default function App() {
  const [boot, setBoot] = useState<Boot>();
  const [session, setSession] = useState<LearningSession>();
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(0);
  const [mode, setMode] = useState<Mode>('welcome');
  const [panel, setPanel] = useState<Panel>('solve');
  const [beatIndex, setBeatIndex] = useState(0);
  const [autoAdvance, setAutoAdvance] = useState(true);
  const [voiceReplies, setVoiceReplies] = useState(true);
  const [tray, setTray] = useState<Tray>('ask');
  const [trayOpen, setTrayOpen] = useState(false);
  const [recordOpen, setRecordOpen] = useState(false);
  const [operation, setOperation] =
    useState<AlgebraOperation['kind']>('subtract');
  const [amount, setAmount] = useState('');
  const [candidate, setCandidate] = useState('');
  const [unit, setUnit] = useState('');
  const [question, setQuestion] = useState('');
  const [drafts, setDrafts] = useState<Partial<Record<StageId, string>>>({});
  const [selectedTerm, setSelectedTerm] = useState('');
  const [inlineAnswer, setInlineAnswer] = useState(true);
  const [spokenProposal, setSpokenProposal] = useState<AlgebraOperation>();
  const reducedMotion = useReducedMotion();
  const tutorAbort = useRef<AbortController | undefined>(undefined);
  const [reply, setReply] = useState<{
    text: string;
    provider: string;
    revision: number;
  }>();
  const [tutorBusy, setTutorBusy] = useState(false);
  const [audioBusy, setAudioBusy] = useState(false);
  const initial = useRef<Promise<Boot> | undefined>(undefined);
  const sessionRef = useRef<LearningSession | undefined>(undefined);
  const queue = useRef<Promise<unknown>>(Promise.resolve());
  const replyGeneration = useRef(0);
  const questionRef = useRef<HTMLTextAreaElement>(null);
  const trayRef = useRef<HTMLDialogElement>(null);
  const update = useCallback((next: LearningSession) => {
    sessionRef.current = next;
    setSession(next);
  }, []);
  useEffect(() => {
    let active = true;
    initial.current ??= bootstrap();
    initial.current
      .then((data) => {
        if (!active) return;
        setBoot(data);
        if (!sessionRef.current) {
          update(data.session);
          const completed =
            data.session.teaching?.lessonVersion === TEACHING_VERSION &&
            data.session.teaching.completed;
          setMode(completed ? 'practice' : 'welcome');
          setBeatIndex(
            Math.max(
              0,
              teachingBeats.findIndex(
                (b) => b.id === data.session.teaching?.lastBeatId,
              ),
            ),
          );
          setPanel(
            data.session.activeStage === 'concept' ||
              hasModel(data.session.stages[data.session.activeStage])
              ? 'solve'
              : 'model',
          );
        }
      })
      .catch((err) => {
        if (active) setError(err.message);
      });
    return () => {
      active = false;
    };
  }, [update]);
  function enqueue<T>(work: () => Promise<T>): Promise<T | undefined> {
    setBusy((n) => n + 1);
    const job = queue.current
      .then(work)
      .catch(async (err) => {
        if (err instanceof Error && err.name === 'AbortError') return undefined;
        setError(
          err instanceof Error
            ? err.message
            : 'Something went wrong. Your draft is still here.',
        );
        if (
          err instanceof ApiError &&
          err.status === 409 &&
          sessionRef.current
        ) {
          update(
            await api<LearningSession>(
              `/api/sessions/${sessionRef.current.id}`,
            ),
          );
          setError(
            'This lesson changed in another window. We loaded the saved version. Check it before trying again.',
          );
        }
        return undefined;
      })
      .finally(() => setBusy((n) => Math.max(0, n - 1)));
    queue.current = job.catch(() => undefined);
    return job;
  }
  function send(action: SessionAction, quiet = false) {
    if (!quiet) {
      setSpokenProposal(undefined);
      interrupt();
      setError('');
      setStatus('');
    }
    return enqueue(async () => {
      const current = sessionRef.current;
      if (!current) throw new Error('Your lesson is still loading.');
      const next = await api<LearningSession>(
        `/api/sessions/${current.id}/actions`,
        {
          method: 'POST',
          body: JSON.stringify({
            expectedRevision: current.revision,
            commandId: commandId(),
            action,
          }),
        },
      );
      update(next);
      return next;
    });
  }
  function exposure(event: Exposure) {
    const stageId = teachingBeats.some((b) => b.audioId === event.audioId)
      ? 'concept'
      : stages.find(
          (id) => boot?.lesson.stages[id].audioIds.intro === event.audioId,
        );
    void send({ type: 'playback', stageId, ...event }, true);
  }
  const voice = useTutorVoice({
    onExposure: exposure,
    onLessonEnd: (id) => {
      const index = teachingBeats.findIndex((b) => b.audioId === id);
      if (mode !== 'teach' || index !== beatIndex) return;
      if (index === teachingBeats.length - 1) void finishTeaching(false);
      else if (autoAdvance && !trayOpen && !recordOpen) goBeat(index + 1, true);
    },
  });
  function interrupt() {
    replyGeneration.current++;
    tutorAbort.current?.abort();
    setTutorBusy(false);
    setAudioBusy(false);
    voice.pause();
  }
  function openTray(next: Tray) {
    interrupt();
    setTray(next);
    setTrayOpen(true);
  }
  function resumeTeaching() { interrupt(); voice.resumeLesson(); }
  function replayVoice() { interrupt(); voice.replay(); }
  const recorder = useRecorder({
    onSpeechStart: () => {
      interrupt();
      setStatus('I’m listening. Your board stays here.');
    },
    onComplete: async (wav, signal) => {
      const result = await api<{ text: string }>('/api/transcribe', {
        method: 'POST',
        headers: { 'Content-Type': 'audio/wav' },
        body: wav,
        signal,
      });
      if (signal.aborted) return;
      setQuestion(result.text);
      if (!result.text)
        setStatus('No words were recognized. Try again or type.');
      else await handleUtterance(result.text, signal);
    },
  });
  async function handleUtterance(text: string, signal?: AbortSignal) {
    if (signal?.aborted) return;
    const current = sessionRef.current;
    if (!current) return;
    const command = text.trim().toLowerCase().replace(/[.!?]/g, '');
    if (/^(pause|stop talking|wait)( please)?$/.test(command)) { interrupt(); return; }
    if (/^(resume|continue teaching|keep going)( please)?$/.test(command)) { resumeTeaching(); return; }
    if (/^(repeat|say that again|replay)( please)?$/.test(command)) { replayVoice(); return; }
    if (/^(teach me|show me how)( please)?$/.test(command)) { goBeat(0, true); return; }
    if (/^(next part|next step)( please)?$/.test(command) && mode === 'teach') { goBeat(Math.min(teachingBeats.length-1,beatIndex+1),true); return; }
    if (mode === 'practice' && current.activeStage !== 'concept' && /^(?:i think )?(?:x|the unknown) (?:means|is|stands for|represents) (?:the )?(?:number of )?hours(?: rented)?$/.test(command)) {
      const next = await send({type:'unknownAnswer',choiceId:'hours-rented'});
      if (next) { setStatus('Recorded: x means hours rented. Build your equation on the board.'); setPanel('model'); }
      return;
    }
    if (/^(check my answer|check my value)( please)?$/.test(command) && mode === 'practice' && panel === 'check' && candidate.trim()) {
      await send({type:'checkCandidate',value:candidate,...(current.activeStage==='concept'?{}:{unit})}); return;
    }
    if (/^(check my model|check my equation)( please)?$/.test(command) && mode === 'practice' && current.activeStage !== 'concept') {
      await checkModel(); return;
    }
    if (/^(apply (that|this)( operation)?|yes apply it|confirm the move)$/.test(command) && spokenProposal) {
      const next = await send({ type: 'applyOperation', operation: spokenProposal });
      if (next) { setPanel('solve'); setSpokenProposal(undefined); setQuestion(''); setStatus('Applied to both entire sides.'); }
      return;
    }
    const math = detectSpokenMath(text);
    if (math.kind === 'equation' && mode === 'practice' && panel === 'check' && math.equation.left.x === '1' && math.equation.left.constant === '0' && math.equation.right.x === '0') {
      setCandidate(math.equation.right.constant);
      setStatus(`I heard x = ${math.equation.right.constant}. Check the value and units, then say “check my answer.”`); return;
    }
    if (math.kind === 'equation' && mode === 'practice' && current.activeStage !== 'concept') {
      setDrafts(d => ({...d,[current.activeStage]:math.draftEquation}));
      setPanel('model'); setSelectedTerm(''); setReply(undefined);
      setStatus(`I heard: ${math.draftEquation}. Edit any part, then say “check my model.”`);
      return;
    }
    const move = interpretOperation(text);
    if (move && mode === 'practice') {
      setOperation(move.kind); setAmount(move.value); setSpokenProposal(move);
      setStatus(`Proposed: ${move.kind} ${move.value} on both sides. Say “apply that” to confirm.`); return;
    }
    if (math.kind === 'value' && mode === 'practice' && panel === 'check') {
      setCandidate(math.value); if (math.unit && math.unit.startsWith('hour')) setUnit('hours');
      setStatus(`I heard x = ${math.value}. Check the value and units before submitting.`); return;
    }
    await askTutor(undefined,text,signal);
  }
  async function checkModel() {
    const current=sessionRef.current;
    if (!current || current.activeStage === 'concept') return;
    const equation=drafts[current.activeStage] ?? current.stages[current.activeStage].modelEquation?.draftEquation ?? '';
    if (!equation.trim()) { setStatus('Build or say your equation first.'); return; }
    const next=await send({type:'submitModelEquation',equation});
    if(next) setStatus(next.stages[current.activeStage].modelEquation?.feedback ?? 'Your model was checked.');
  }
  useEffect(() => {
    if (trayOpen) trayRef.current?.showModal();
    else trayRef.current?.close();
  }, [trayOpen]);
  useEffect(() => {
    const main = document.getElementById('lesson-main');
    if (main) main.scrollTop = 0;
  }, [mode, panel, session?.activeStage]);
  function goBeat(index: number, autoplay: boolean) {
    interrupt();
    setSpokenProposal(undefined);
    void recorder.cancelTurn();
    if (sessionRef.current?.activeStage !== 'concept') {
      void send({ type: 'setStage', stageId: 'concept' }, true);
    }
    setMode('teach');
    setBeatIndex(index);
    setReply(undefined);
    setStatus('');
    const beat = teachingBeats[index];
    void send(
      {
        type: 'teachingProgress',
        lessonVersion: TEACHING_VERSION,
        beatId: beat.id,
        event: 'viewed',
      },
      true,
    );
    voice.lesson(
      boot?.manifest?.segments.find((s) => s.id === beat.audioId),
      autoplay,
    );
  }
  async function finishTeaching(enterPractice: boolean) {
    if (enterPractice) recorder.cancelTurn();
    const allVisited = teachingBeats.every((b) =>
      sessionRef.current?.teaching?.viewedBeatIds.includes(b.id),
    );
    if (allVisited)
      await send(
        {
          type: 'teachingProgress',
          lessonVersion: TEACHING_VERSION,
          beatId: teachingBeats.at(-1)!.id,
          event: 'completed',
        },
        true,
      );
    if (enterPractice) {
      interrupt();
      voice.clear();
      setMode('practice');
      setPanel(
        sessionRef.current?.activeStage === 'concept' ||
          (sessionRef.current && hasModel(sessionRef.current.stages[sessionRef.current.activeStage]))
          ? 'solve'
          : 'model',
      );
      setStatus('Your turn. Choose a change that preserves equality.');
    }
  }
  function skipTeaching() {
    interrupt();
    recorder.cancelTurn();
    voice.skip();
    void send(
      {
        type: 'teachingProgress',
        lessonVersion: TEACHING_VERSION,
        beatId: teachingBeats[beatIndex].id,
        event: 'skipped',
      },
      true,
    );
    voice.clear();
    setMode('practice');
    setPanel(
      session?.activeStage === 'concept' ||
        (session && hasModel(session.stages[session.activeStage]))
        ? 'solve'
        : 'model',
    );
    setStatus(
      'You can return to the worked lesson anytime. We will check your reasoning during practice.',
    );
  }
  async function changeStage(id: StageId) {
    interrupt();
    recorder.cancelTurn();
    voice.clear();
    const next = await send({ type: 'setStage', stageId: id });
    if (!next) return;
    setMode('practice');
    setPanel(
      id === 'concept' || hasModel(next.stages[id])
        ? 'solve'
        : 'model',
    );
    setCandidate('');
    setUnit('');
    setAmount('');
    setQuestion('');
    setReply(undefined);
    if (id !== 'concept')
      voice.lesson(
        boot?.manifest?.segments.find(
          (s) => s.id === boot.lesson.stages[id].audioIds.intro,
        ),
        true,
      );
    requestAnimationFrame(() =>
      document.getElementById('lesson-main')?.focus(),
    );
  }
  async function askTutor(event?: FormEvent, spoken?: string, signal?: AbortSignal) {
    event?.preventDefault();
    const text = (spoken ?? question).trim();
    if (!text || signal?.aborted) return;
    const replyPlayback =
      voice.source?.kind === 'reply' && reply
        ? { tutorRevision: reply.revision, positionMs: voice.position }
        : undefined;
    interrupt();
    const abort = new AbortController();
    tutorAbort.current = abort;
    const abortTurn = () => abort.abort();
    signal?.addEventListener('abort', abortTurn, { once: true });
    setError('');
    setReply(undefined);
    setInlineAnswer(true);
    setTutorBusy(true);
    const generation = ++replyGeneration.current;
    const context = voice.snapshot();
    const teachingBeatId =
      mode === 'teach' ? teachingBeats[beatIndex].id : undefined;
    const result = await enqueue(async () => {
      if (abort.signal.aborted || generation !== replyGeneration.current) throw new DOMException('Interrupted', 'AbortError');
      const current = sessionRef.current!;
      const draftText = drafts[current.activeStage] ?? current.stages[current.activeStage].modelEquation?.draftEquation ?? '';
      const board = parsedDraft(draftText);
      const response = await api<{
        reply: string;
        provider: string;
        revision: number;
      }>(`/api/sessions/${current.id}/tutor`, {
        method: 'POST',
        signal: abort.signal,
        body: JSON.stringify({
          question: text,
          practiceView: panel,
          ...(board ? { boardContext: { draftEquation: board.draftEquation, ...(selectedTerm && board.draftEquation.includes(selectedTerm) ? { selectedTerm } : {}) } } : {}),
          ...(replyPlayback ? { replyPlayback } : {}),
          ...(teachingBeatId ? { teachingBeatId } : {}),
          ...(!replyPlayback && context
            ? {
                playback: {
                  segmentId: context.segmentId,
                  positionMs: context.positionMs,
                },
              }
            : {}),
        }),
      });
      update(await api<LearningSession>(`/api/sessions/${current.id}`));
      if (generation === replyGeneration.current)
        setReply({
          text: response.reply,
          provider: response.provider,
          revision: response.revision,
        });
      return response;
    });
    signal?.removeEventListener('abort', abortTurn);
    if (generation === replyGeneration.current) setTutorBusy(false);
    if (
      result &&
      generation === replyGeneration.current &&
      voiceReplies &&
      boot?.capabilities?.tts.available
    )
      void speakReply(result.reply, generation);
  }
  async function speakReply(
    text: string,
    generation = ++replyGeneration.current,
  ) {
    voice.pause();
    setAudioBusy(true);
    try {
      const abort = new AbortController();
      tutorAbort.current = abort;
      const result = await api<{ url: string; durationMs?: number; words?: {text:string;startMs:number;endMs:number}[]; timingMethod?:string }>('/api/speech', {
        method: 'POST',
        body: JSON.stringify({ text }),
        signal: abort.signal,
      });
      if (generation === replyGeneration.current) voice.reply(result.url, text, result);
    } catch (err) {
      if (generation === replyGeneration.current)
        setError(
          err instanceof Error
            ? err.message
            : 'Voice is unavailable. Your reply is still readable.',
        );
    } finally {
      if (generation === replyGeneration.current) setAudioBusy(false);
    }
  }
  if (!boot || !session)
    return (
      <main className="boot-screen">
        <span className="brand">
          <Icon name="spark" /> enactry
        </span>
        <h1>
          {error
            ? 'The studio could not open.'
            : 'Opening your algebra studio…'}
        </h1>
        <p>{error || 'Loading your saved lesson.'}</p>
        {error && (
          <button className="primary" onClick={() => location.reload()}>
            Try again
          </button>
        )}
      </main>
    );
  const stageId = session.activeStage,
    stage = boot.lesson.stages[stageId],
    progress = session.stages[stageId];
  const beat = teachingBeats[beatIndex];
  const teaching = mode === 'teach',
    isConcept = stageId === 'concept';
  const locked = busy > 0;
  const proposal = interpretOperation(question),
    nextStage = stages[stages.indexOf(stageId) + 1];
  const secureMic =
    !!navigator.mediaDevices?.getUserMedia && window.isSecureContext;
  const micActive = recorder.enabled || recorder.status === 'requesting';
  const currentSegment =
    voice.source?.kind === 'lesson' ? voice.source.segment : undefined;
  const transcript =
    voice.source?.kind === 'reply'
      ? voice.source.text
      : teaching
        ? beat.narration
        : (currentSegment?.text ?? stage.prompt);
  const currentSentence = currentSegment?.checkpoints
    .filter((c) => c.startMs <= voice.position)
    .at(-1)?.text;
  const draftEquation = drafts[stageId] ?? progress.modelEquation?.draftEquation ?? '';
  const sceneSegment = boot.manifest?.segments.find(s => s.id === beat.audioId);
  const lessonPosition = voice.source?.kind === 'lesson' && voice.source.segment.id === beat.audioId ? voice.position : voice.snapshot()?.segmentId === beat.audioId ? voice.snapshot()?.positionMs ?? 0 : 0;
  const baseFrame = lessonFrame(beat.id, sceneSegment, lessonPosition, reducedMotion);
  const focusEquation = panel === 'model' ? parsedDraft(draftEquation)?.equation ?? progress.equation : teaching ? beat.equation : progress.equation;
  const spokenFocus = voice.source?.kind === 'reply' ? replyFocus(voice.source.text, voice.position, voice.duration, focusEquation, voice.source.words) : !teaching && currentSegment ? replyFocus(currentSegment.text,voice.position,currentSegment.durationMs,focusEquation,currentSegment.words) : undefined;
  const liveFrame: SceneFrame = { ...(teaching ? baseFrame : {kind:'practice' as const,phase:0,progress:1,focus:'both' as const,caption:'Every move changes both entire sides. Tap a symbol to ask about it.',word:'',equation:formatEquation(panel==='check'?stage.initialEquation:progress.equation)}), ...(spokenFocus?.word ? {focus:spokenFocus.focus,word:spokenFocus.word} : {}) };
  const boardFocus = spokenFocus?.word ? spokenFocus.normalizedToken : selectedTerm;
  const narratedMove = voice.source?.kind === 'reply' && mode === 'practice' && panel === 'solve' ? narratedOperation(progress.equation, voice.source.words ?? [], voice.position) : undefined;
  const tutorState =
    recorder.status === 'recording'
      ? 'Listening to you'
      : recorder.status === 'listening'
        ? 'Mic on · speak to interrupt'
        : recorder.status === 'transcribing'
          ? 'Hearing your question…'
          : tutorBusy
            ? 'Thinking about your question…'
            : audioBusy
              ? 'Preparing my voice…'
              : voice.playing
                ? 'Speaking · you can interrupt'
                : teaching
                  ? 'Here when you need me'
                  : 'Ask me anything about this step';
  function setPracticePanel(next: Panel) {
    interrupt();
    setSpokenProposal(undefined);
    recorder.cancelTurn();
    setPanel(next);
    setStatus('');
  }
  async function applyOperation(event: FormEvent) {
    event.preventDefault();
    const next = await send({
      type: 'applyOperation',
      operation: { kind: operation, value: amount.trim() },
    });
    if (next) {
      setAmount('');
      setStatus('Changed both entire sides. The solution is preserved.');
    }
  }
  async function checkCandidate(event: FormEvent) {
    event.preventDefault();
    const next = await send({
      type: 'checkCandidate',
      value: candidate,
      ...(isConcept ? {} : { unit }),
    });
    if (next)
      setStatus(
        next.stages[stageId].candidateCheck?.correct &&
          (isConcept || next.stages[stageId].unitAnswer?.correct)
          ? 'That value checks in the original. Now explain your reasoning.'
          : 'That answer needs another look. Check the original equation and your units.',
      );
  }
  const liveConversation = (<aside className="live-conversation" aria-label="Conversation beside your board">
          {(reply || tutorBusy || recorder.enabled) && <div className="live-conversation-heading"><span className="eyebrow">{tutorState}</span>{reply && <button className="text-button" aria-expanded={inlineAnswer} onClick={() => setInlineAnswer(!inlineAnswer)}>{inlineAnswer ? 'Hide answer' : 'Show answer'}</button>}</div>}
          {question && <p className="heard-words"><span>You:</span> {question}</p>}
          {reply && inlineAnswer && <p className="live-reply" aria-live="polite">{reply.text}</p>}
          {spokenProposal && <div className="inline-proposal"><span>Proposed move: <b>{spokenProposal.kind} {spokenProposal.value}</b> on both sides.</span><button className="secondary" disabled={locked} onClick={() => void send({type:'applyOperation',operation:spokenProposal}).then(next=>{if(next){setPanel('solve');setSpokenProposal(undefined);setQuestion('');}})}>Apply that</button><button className="text-button" onClick={()=>setSpokenProposal(undefined)}>Dismiss</button></div>}
          {recorder.error && <p role="alert" className="feedback">{recorder.error}</p>}
          <form className="board-question" onSubmit={e=>{e.preventDefault();void handleUtterance(question);}}><label className="visually-hidden" htmlFor="board-question">Ask or say an equation</label><input id="board-question" value={question} onChange={e=>setQuestion(e.target.value)} placeholder={micActive ? 'Mic is on. Speak anytime, or type here.' : 'Ask a question, or type “I think the equation should be…”'} maxLength={1500}/><button className="secondary" disabled={!question.trim() || busy>0}>{tutorBusy?'Thinking…':'Send'} <Icon name="arrow"/></button></form>
          {voice.source?.kind === 'reply' && <button className="text-button" onClick={resumeTeaching}>Return to the explanation</button>}
        </aside>);
  return (
    <div className="studio">
      <audio {...voice.audioProps} aria-label="AI tutor voice" />
      <a className="skip-link" href="#lesson-main">
        Skip to learning activity
      </a>
      <header className="studio-header">
        <a href="/" className="brand" aria-label="Enactry home">
          <span className="brand-mark">
            <Icon name="spark" />
          </span>
          enactry
        </a>
        <div className="course-label">
          THE ALGEBRA WORKSHOP
          <span>Grade 7 · Texas · Concept + application</span>
        </div>
        <div className="header-tools">
          <span className="save-status">
            <i />
            {busy ? 'Saving…' : 'Preview · progress saved'}
          </span>
          <button
            className="quiet"
            aria-label="Learning record"
            onClick={() => {
              interrupt();
              setRecordOpen(true);
            }}
          >
            <Icon name="book" />
            <span>Learning record</span>
          </button>
        </div>
      </header>
      <nav className="journey" aria-label="Learning stages">
        {stages.map((id, i) => (
          <button
            key={id}
            aria-current={id === stageId ? 'step' : undefined}
            disabled={locked}
            onClick={() => void changeStage(id)}
          >
            <span className="step-number">
              {session.stages[id].complete ? (
                <Icon name="check" />
              ) : (
                `0${i + 1}`
              )}
            </span>
            <span>{names[id]}</span>
            {i < 2 && <Icon name="arrow" />}
          </button>
        ))}
        <span className="journey-note">One idea. Make it yours.</span>
      </nav>
      <main
        id="lesson-main"
        className={`learning-viewport mode-${mode}`}
        tabIndex={-1}
      >
        <div className="activity-heading">
          <div>
            <span className="eyebrow">
              {mode === 'welcome'
                ? 'A little guidance. A lot of discovery.'
                : teaching
                  ? `Worked lesson · ${beatIndex + 1} of ${teachingBeats.length} · ${beat.label}`
                  : `${names[stageId]} · ${panel === 'model' ? 'Build a model' : panel === 'solve' ? 'Explore the equation' : 'Check & explain'}`}
            </span>
            <h1>
              {mode === 'welcome'
                ? 'Make the unknown make sense.'
                : teaching
                  ? beat.title
                  : panel === 'model'
                    ? 'Turn the story into a relationship.'
                    : panel === 'check'
                      ? 'Make your reasoning visible.'
                      : isConcept
                        ? 'The same value, two expressions.'
                        : 'What does your model reveal?'}
            </h1>
          </div>
          {mode !== 'welcome' && (
            <button
              className="quiet teach-again"
              disabled={locked}
              onClick={() => goBeat(0, true)}
            >
              <Icon name="replay" />
              <span>{teaching ? 'Restart lesson' : 'Teach me'}</span>
            </button>
          )}
        </div>
        {mode === 'welcome' ? (
          <section className="welcome-stage">
            <div className="welcome-copy">
              <span className="lesson-chip">
                <Icon name="leaf" /> Your AI tutor is here
              </span>
              <h2>
                See it. Understand it.
                <br />
                <em>Try it yourself.</em>
              </h2>
              <p>
                Start with a guided example. I’ll show what the symbols mean,
                explain each move, and connect the idea to a real situation.
              </p>
              <div className="welcome-actions">
                <button
                  className="primary"
                  disabled={locked}
                  onClick={() => goBeat(0, true)}
                >
                  <Icon name="play" /> Teach me the idea <Icon name="arrow" />
                </button>
                {session.teaching?.viewedBeatIds.length ? (
                  <button
                    className="secondary"
                    disabled={locked}
                    onClick={() => goBeat(beatIndex, true)}
                  >
                    Continue explanation
                  </button>
                ) : null}
                <button
                  className="text-button"
                  disabled={locked}
                  onClick={skipTeaching}
                >
                  I’m ready to try <Icon name="arrow" />
                </button>
              </div>
              <span className="microcopy">
                7 short parts · your pace · no timer
              </span>
            </div>
            <div className="welcome-art">
              <div className="welcome-orbit" />
              <div className="floating-equation">
                <span>2x + 4</span>
                <b>=</b>
                <span>10</span>
              </div>
              <div className="welcome-tutor">
                <TutorAvatar pointing />
              </div>
              <div className="hello-bubble">
                <span>Hello, curious mind.</span>
                <p>Let’s make this click.</p>
              </div>
              <span className="art-label">
                An explanation you can interrupt.
              </span>
            </div>
          </section>
        ) : teaching ? (
          <section
            className={`teaching-stage focus-${beat.focus}`}
            aria-label="Worked example"
          >
            <AnimatedBoard frame={liveFrame} speaking={voice.playing} reduced={reducedMotion}
              onPoint={term => {setSelectedTerm(term); void askTutor(undefined, `Explain ${term === 'x' ? 'the unknown x' : term === 'constant' ? 'the added number' : term === 'total' ? 'the right side' : 'the equals sign'} in this example.`);}}
              label={beat.id === 'your-turn' ? 'Your practice equation' : 'Watch the worked example'}/>
          </section>
        ) : (
          <div className={`practice-stage panel-${panel}`}>
            <div className="practice-scene">
              {!isConcept && panel === 'model' ? (
                <EquationWorkshop value={draftEquation} onChange={value => {setDrafts(d => ({...d,[stageId]:value}));setStatus('');}}
                  onCheck={() => void checkModel()} transfer={stageId === 'transfer'} speaking={voice.playing}
                  focusTerm={boardFocus} onSelect={setSelectedTerm} disabled={locked}
                  feedback={progress.modelEquation?.draftEquation === parsedDraft(draftEquation)?.draftEquation ? progress.modelEquation?.feedback : undefined}
                  correct={progress.modelEquation?.correct && progress.modelEquation.draftEquation === parsedDraft(draftEquation)?.draftEquation}/>
              ) : (
                <AnimatedBoard frame={liveFrame} equation={panel === 'check' ? stage.initialEquation : progress.equation}
                  speaking={voice.playing} reduced={reducedMotion} label={panel === 'check' ? 'Check the original relationship' : 'Your equation workspace'}
                  onPoint={term => {setSelectedTerm(term);void askTutor(undefined,`Explain ${term === 'x' ? 'x' : term === 'constant' ? 'the added number' : term === 'total' ? 'the right side' : 'the equals sign'} in my current equation.`);}}>
                  {narratedMove ? <div className="narrated-preview"><p>Tutor demonstration · your equation stays unchanged</p><OperationDemonstration {...narratedMove} reduced={reducedMotion} mediaProgress={reducedMotion?1:narratedMove.progress}/></div> : panel === 'solve' && progress.history.at(-1) && <OperationDemonstration key={progress.history.at(-1)!.at} before={progress.history.at(-1)!.equation} operation={progress.history.at(-1)!.operation} after={progress.equation} reduced={reducedMotion}/>}
                  {panel !== 'check' && progress.history.length > 0 && <div className="board-history"><span>Both sides changed together.</span><button className="text-button" disabled={locked} onClick={() => void send({type:'undo'})}>Undo last move</button></div>}
                </AnimatedBoard>
              )}
              {!isConcept && panel !== 'model' && (
                <div className="story-recap">
                  <Icon name="route" />
                  <span>
                    {stageId === 'transfer'
                      ? '$8 starting charge · $4 per hour · $20 total'
                      : '$6 starting charge · $3 per hour · $18 total'}
                  </span>
                  <button
                    className="text-button"
                    onClick={() => setPracticePanel('model')}
                  >
                    See story
                  </button>
                </div>
              )}
              <div className="practice-companion">
                <div>
                  <span className="eyebrow">Your AI tutor</span>
                  <p>
                    {progress.complete
                      ? 'You made the relationship make sense.'
                      : panel === 'model'
                        ? 'What changes, and what stays fixed?'
                        : panel === 'check'
                          ? 'A good explanation shows why.'
                          : 'Your ideas drive this. I’m here to help.'}
                  </p>
                </div>
                <button
                  className="round"
                  aria-label="Ask your AI tutor"
                  onClick={() => openTray('ask')}
                >
                  <Icon name="spark" />
                </button>
              </div>
            </div>
            <section className="task-card" aria-label="Current learning task">
              <nav className="task-tabs" aria-label="Activity views">
                {!isConcept && (
                  <button
                    aria-current={panel === 'model' ? 'page' : undefined}
                    onClick={() => setPracticePanel('model')}
                  >
                    Model
                  </button>
                )}
                <button
                  aria-current={panel === 'solve' ? 'page' : undefined}
                  disabled={!isConcept && !hasModel(progress)}
                  onClick={() => setPracticePanel('solve')}
                >
                  Explore
                </button>
                <button
                  aria-current={panel === 'check' ? 'page' : undefined}
                  disabled={!isConcept && !hasModel(progress)}
                  onClick={() => setPracticePanel('check')}
                >
                  Check & explain
                </button>
              </nav>
              <div className="task-content">
                {panel === 'model' ? (
                  <>
                    {!progress.unknownAnswer?.correct ? (
                      <>
                        <span className="eyebrow">Define the unknown</span>
                        <h2>{stage.unknownChoice}</h2>
                        <p>
                          The prices are known. Choose what the story is asking
                          you to find.
                        </p>
                        <Choices
                          options={stage.unknownOptions}
                          selected={progress.unknownAnswer?.choiceId}
                          disabled={locked}
                          onChoose={(choiceId) =>
                            void send({ type: 'unknownAnswer', choiceId })
                          }
                        />
                        {progress.unknownAnswer &&
                          !progress.unknownAnswer.correct && (
                            <p className="feedback">
                              That quantity is already given. Which one is still
                              unknown?
                            </p>
                          )}
                      </>
                    ) : (
                      <>
                        <span className="eyebrow">
                          Connect words to symbols
                        </span>
                        <h2>Build the relationship.</h2>
                        <p>x means hours rented. Use the whiteboard: the starting charge happens once; the hourly charge repeats.</p>
                        <p className="microcopy">Say “I think the equation should be…” followed by your equation. Then say “check my model.”</p>
                        {hasModel(progress) && <button className="primary" onClick={() => setPracticePanel('solve')}>Explore this model <Icon name="arrow" /></button>}
                      </>
                    )}
                  </>
                ) : panel === 'solve' ? (
                  <>
                    <span className="eyebrow">Your next move</span>
                    <h2>Keep both sides equal.</h2>
                    <p>
                      Choose a change. Watch what happens to both entire
                      expressions.
                    </p>
                    <form onSubmit={applyOperation} className="operation-form">
                      <label>
                        Operation
                        <select
                          value={operation}
                          onChange={(e) =>
                            setOperation(
                              e.target.value as AlgebraOperation['kind'],
                            )
                          }
                          disabled={locked}
                        >
                          <option value="subtract">Subtract</option>
                          <option value="add">Add</option>
                          <option value="divide">Divide by</option>
                          <option value="multiply">Multiply by</option>
                        </select>
                      </label>
                      <label>
                        Value
                        <input
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          placeholder="e.g. 2 or 1/2"
                          disabled={locked}
                          autoComplete="off"
                        />
                      </label>
                      <button
                        className="primary"
                        disabled={locked || !amount.trim()}
                      >
                        Apply to both sides <Icon name="arrow" />
                      </button>
                    </form>
                    <p className="microcopy">
                      Add or subtract a number or an x-term. Divide or multiply
                      by a nonzero number.
                    </p>
                    {progress.history.length > 0 && (
                      <div className="last-move">
                        <Icon name="check" />
                        <span>
                          {progress.history.length}{' '}
                          {progress.history.length === 1 ? 'move' : 'moves'}{' '}
                          recorded ·{' '}
                          <button
                            className="text-button"
                            onClick={() => openTray('history')}
                          >
                            See your steps
                          </button>
                        </span>
                      </div>
                    )}
                    <button
                      className="secondary"
                      onClick={() => setPracticePanel('check')}
                    >
                      Ready to check my value <Icon name="arrow" />
                    </button>
                  </>
                ) : (
                  <>
                    <span className="eyebrow">Check in the original</span>
                    <h2>What value makes it true?</h2>
                    <form onSubmit={checkCandidate} className="check-form">
                      <label>
                        Value of x
                        <input
                          value={candidate}
                          onChange={(e) => setCandidate(e.target.value)}
                          placeholder="Your value"
                          disabled={locked}
                        />
                      </label>
                      {!isConcept && (
                        <label>
                          Units
                          <select
                            value={unit}
                            onChange={(e) => setUnit(e.target.value)}
                            disabled={locked}
                          >
                            <option value="">Choose units</option>
                            {stage.unitOptions.map((o) => (
                              <option key={o.id} value={o.id}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </label>
                      )}
                      <button
                        className="primary"
                        disabled={
                          locked || !candidate.trim() || (!isConcept && !unit)
                        }
                      >
                        Check answer <Icon name="check" />
                      </button>
                    </form>
                    {progress.candidateCheck && (
                      <div
                        className={`check-result ${progress.candidateCheck.correct ? 'correct' : ''}`}
                      >
                        <strong>
                          {progress.candidateCheck.correct
                            ? 'The substitution works.'
                            : 'The original does not check yet.'}
                        </strong>
                        <span>
                          {substitutionText(
                            stage.initialEquation,
                            progress.candidateCheck.value,
                          )}
                        </span>
                        {!isConcept && !progress.unitAnswer?.correct && (
                          <span>Check the units: what did x stand for?</span>
                        )}
                      </div>
                    )}
                    {progress.candidateCheck?.correct && (
                      <>
                        <h3>Why does your reasoning work?</h3>
                        <Choices
                          options={stage.explanationOptions}
                          selected={progress.explanationAnswer?.choiceId}
                          disabled={locked}
                          onChoose={(choiceId) =>
                            void send({ type: 'explain', choiceId })
                          }
                        />
                        {progress.explanationAnswer &&
                          !progress.explanationAnswer.correct && (
                            <p className="feedback">
                              {isConcept
                                ? 'Consider the entire expression on each side, not just the x-term.'
                                : 'Explain what the number means, with units, and check it against the starting charge and hourly cost.'}
                            </p>
                          )}
                        {!progress.complete && (
                          <p className="microcopy">
                            Complete an equation trail that isolates x, check
                            the original, and choose a valid explanation
                            {!isConcept ? ' with the correct units' : ''}.
                          </p>
                        )}
                      </>
                    )}
                    {progress.complete && (
                      <div className="completion">
                        <Icon name="check" />
                        <strong>This activity is complete.</strong>
                        <p>
                          Your next step uses the idea in a different situation.
                        </p>
                        {nextStage ? (
                          <button
                            className="primary"
                            disabled={locked}
                            onClick={() => void changeStage(nextStage)}
                          >
                            Continue to {names[nextStage]} <Icon name="arrow" />
                          </button>
                        ) : (
                          <button
                            className="primary"
                            onClick={() => {
                              interrupt();
                              setRecordOpen(true);
                            }}
                          >
                            See my learning record <Icon name="book" />
                          </button>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
              {liveConversation}
            </section>
          </div>
        )}
        {teaching && liveConversation}
      </main>
      <footer className="lesson-footer">
        {teaching ? (
          <div className="teaching-navigation">
            <button
              className="quiet"
              disabled={beatIndex === 0 || locked}
              onClick={() => goBeat(beatIndex - 1, voice.playing)}
            >
              <span>←</span>
              <span>Back</span>
            </button>
            <div className="beat-dots" aria-label="Explanation parts">
              {teachingBeats.map((b, i) => (
                <button
                  key={b.id}
                  aria-label={`Part ${i + 1}: ${b.label}`}
                  aria-current={i === beatIndex ? 'step' : undefined}
                  disabled={locked}
                  onClick={() => goBeat(i, voice.playing)}
                >
                  <span />
                </button>
              ))}
            </div>
            <button
              className="primary"
              disabled={locked}
              onClick={() =>
                beatIndex < teachingBeats.length - 1
                  ? goBeat(beatIndex + 1, voice.playing)
                  : void finishTeaching(true)
              }
            >
              {beatIndex < teachingBeats.length - 1
                ? 'Next part'
                : 'Try it myself'}
              <Icon name="arrow" />
            </button>
            <button
              className="text-button skip-explanation"
              disabled={locked}
              onClick={skipTeaching}
            >
              Go to practice
            </button>
          </div>
        ) : mode === 'practice' ? (
          <div className="practice-footer">
            <span>Texas grade 7 · two-step equations</span>
            <button
              className="text-button"
              disabled={locked}
              onClick={() => {
                void send({ type: 'restartStage' }).then((next) => {
                  if (next) {
                    setPanel(isConcept ? 'solve' : 'model');
                    setDrafts(d => ({...d,[stageId]:''}));
                    setCandidate('');
                    setUnit('');
                    setAmount('');
                  }
                });
              }}
            >
              <Icon name="replay" /> Reset this activity
            </button>
          </div>
        ) : (
          <div className="practice-footer">
            <span>Understand the concept. Use it in the world.</span>
            <span>Internal learning preview</span>
          </div>
        )}
        <div className="tutor-dock">
          <button
            className="dock-identity"
            onClick={() => openTray('ask')}
            aria-label="Open AI tutor"
          >
            <span>
              <strong>
                Enactry <span>AI tutor</span>
              </strong>
              <small>{tutorState}</small>
            </span>
          </button>
          <div className="dock-caption" aria-live="off">
            {voice.playing && voice.source?.kind === 'reply'
              ? reply?.text || 'Answering your question.'
              : teaching
                ? currentSentence || beat.takeaway
                : status || 'You can ask, listen, or explore at your own pace.'}
          </div>
          <div className="dock-controls">
            {voice.source && (
              <button
                className="round"
                aria-label={voice.playing ? 'Pause tutor' : 'Play tutor'}
                onClick={() =>
                  voice.playing ? interrupt() : void voice.play()
                }
              >
                <Icon name={voice.playing ? 'pause' : 'play'} />
              </button>
            )}
            <button
              className={`round mic-button ${micActive ? 'active' : ''}`}
              aria-label={
                micActive ? 'Stop microphone' : 'Speak to interrupt the tutor'
              }
              disabled={
                !secureMic ||
                !boot.capabilities?.stt.available
              }
              onClick={() =>
                micActive ? void recorder.stop() : void recorder.start()
              }
              title={
                !secureMic
                  ? 'Remote microphone needs HTTPS. Typed questions work here.'
                  : 'Enable microphone; speaking pauses the tutor'
              }
            >
              <Icon name={micActive ? 'stop' : 'mic'} />
            </button>
            <button
              className="primary ask-button"
              onClick={() => openTray('ask')}
            >
              <Icon name="spark" /> Conversation
            </button>
            <button
              className="round"
              aria-label="Tutor voice and playback settings"
              onClick={() => openTray('settings')}
            >
              <Icon name="settings" />
            </button>
          </div>
        </div>
      </footer>
      <dialog
        ref={trayRef}
        className="tutor-tray"
        onCancel={() => {
          interrupt();
          setTrayOpen(false);
        }}
        onClose={() => setTrayOpen(false)}
      >
        <div className="panel-heading">
          <div className="tray-identity">
            <div>
              <span className="eyebrow">Enactry · AI tutor</span>
              <h2>
                {tray === 'ask'
                  ? 'Let’s think it through.'
                  : tray === 'transcript'
                    ? 'Follow the explanation.'
                    : tray === 'history'
                      ? 'Your reasoning, step by step.'
                      : 'Make this space yours.'}
              </h2>
            </div>
          </div>
          <button
            className="round"
            aria-label="Close tutor panel"
            onClick={() => {
              interrupt();
              setTrayOpen(false);
            }}
          >
            <Icon name="close" />
          </button>
        </div>
        <nav className="tray-tabs" aria-label="Tutor tools">
          {(['ask', 'transcript', 'history', 'settings'] as Tray[]).map((t) => (
            <button
              key={t}
              aria-current={tray === t ? 'page' : undefined}
              onClick={() => setTray(t)}
            >
              {t === 'ask'
                ? 'Conversation'
                : t === 'transcript'
                  ? 'Transcript'
                  : t === 'history'
                    ? 'Your steps'
                    : 'Voice & controls'}
            </button>
          ))}
        </nav>
        <div className="tray-content">
          {tray === 'ask' ? (
            <>
              <p className="context-line">
                {teaching
                  ? `We’re discussing: ${beat.title} ${formatEquation(beat.equation)}`
                  : `We’re discussing: ${formatEquation(panel === 'check' ? stage.initialEquation : progress.equation)}`}
              </p>
              <p className="microcopy preview-disclosure">
                Preview: use fictional questions. The HTTPS studio keeps records
                private to this browser. Voice runs on the Enactry server;
                family accounts and parent consent are still being developed.
              </p>
              <form onSubmit={askTutor}>
                <label htmlFor="tutor-question">Your question or idea</label>
                <textarea
                  id="tutor-question"
                  ref={questionRef}
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="Why do we change both sides?"
                  rows={3}
                />
                <div className="conversation-actions">
                  <button
                    type="button"
                    className="secondary"
                    disabled={
                      !secureMic ||
                      !boot.capabilities?.stt.available
                    }
                    onClick={() =>
                      micActive ? void recorder.stop() : void recorder.start()
                    }
                  >
                    <Icon name={micActive ? 'stop' : 'mic'} />
                    {micActive ? 'Turn mic off' : 'Turn mic on'}
                  </button>
                  <button
                    className="primary"
                    disabled={locked || !question.trim()}
                  >
                    {tutorBusy ? 'Thinking…' : 'Ask tutor'}
                    <Icon name="arrow" />
                  </button>
                </div>
              </form>
              {!secureMic && (
                <p className="note">
                  Microphone access requires HTTPS on a remote device. You can
                  type here or{' '}
                  <a
                    href="https://enactry.app.gacud.com/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    open the HTTPS studio
                  </a>
                  , which has a separate saved session.
                </p>
              )}
              {recorder.status !== 'idle' && (
                <p role="status" className="feedback">
                  {tutorState}{' '}
                  {micActive && (
                    <button
                      className="text-button"
                      onClick={() => void recorder.stop()}
                    >
                      Turn mic off
                    </button>
                  )}
                </p>
              )}
              {recorder.error && (
                <p role="alert" className="feedback">
                  {recorder.error}
                </p>
              )}
              {proposal && mode === 'practice' && (
                <div className="proposal">
                  <strong>Confirm this move</strong>
                  <p>
                    {proposal.kind} {proposal.value} on both entire sides.
                  </p>
                  <button
                    className="secondary"
                    disabled={locked}
                    onClick={() =>
                      void send({
                        type: 'applyOperation',
                        operation: proposal,
                      }).then((next) => {
                        if (next) {
                          setPanel('solve');
                          setTrayOpen(false);
                          setQuestion('');
                        }
                      })
                    }
                  >
                    Apply this operation
                  </button>
                </div>
              )}
              {reply && (
                <section className="tutor-reply" aria-label="Tutor answer">
                  <span className="eyebrow">
                    {reply.provider.toLowerCase().includes('fallback')
                      ? 'Prepared guidance'
                      : 'Answer to your question'}
                  </span>
                  <p>{reply.text}</p>
                  <button
                    className="text-button"
                    disabled={audioBusy || !boot.capabilities?.tts.available}
                    onClick={() =>
                      voice.playing ? interrupt() : void speakReply(reply.text)
                    }
                  >
                    <Icon name={voice.playing ? 'pause' : 'sound'} />
                    {voice.playing
                      ? 'Pause answer'
                      : audioBusy
                        ? 'Preparing voice…'
                        : 'Hear this answer'}
                  </button>
                </section>
              )}
              <div className="hint-inline">
                <button
                  className="text-button"
                  disabled={locked}
                  onClick={() => void send({ type: 'hint' })}
                >
                  <Icon name="leaf" /> Get a hint for my activity
                </button>
                {progress.hintsUsed > 0 && (
                  <p>
                    {
                      stage.hints[
                        Math.min(progress.hintsUsed, stage.hints.length) - 1
                      ]
                    }
                  </p>
                )}
              </div>
              <p className="microcopy">
                Questions use your hosted tutor. Voice is processed by the
                Enactry server. AI guidance can be mistaken; equation checks use
                exact rules.
              </p>
            </>
          ) : tray === 'transcript' ? (
            <>
              <span className="lesson-chip">
                {voice.source?.kind === 'reply'
                  ? 'Current tutor answer'
                  : 'Prepared lesson · reusable narration'}
              </span>
              <p className="transcript">
                {currentSegment &&
                voice.source?.kind === 'lesson' &&
                currentSegment.words.length
                  ? currentSegment.words.map((w, i) => (
                      <span
                        key={i}
                        className={
                          voice.position >= w.startMs &&
                          voice.position < w.endMs
                            ? 'spoken-word'
                            : ''
                        }
                      >
                        {w.text}{' '}
                      </span>
                    ))
                  : transcript}
              </p>
              {currentSegment && (
                <>
                  <label htmlFor="voice-position">
                    Narration position · {clock(voice.position)} /{' '}
                    {clock(currentSegment.durationMs)}
                  </label>
                  <input
                    id="voice-position"
                    type="range"
                    min={0}
                    max={currentSegment.durationMs}
                    step={100}
                    value={Math.min(voice.position, currentSegment.durationMs)}
                    onChange={(e) => voice.seek(Number(e.target.value))}
                  />
                  <div className="conversation-actions">
                    <button
                      className="secondary"
                      onClick={() =>
                        voice.playing ? interrupt() : void voice.play()
                      }
                    >
                      <Icon name={voice.playing ? 'pause' : 'play'} />
                      {voice.playing ? 'Pause tutor' : 'Play tutor'}
                    </button>
                    <button className="text-button" onClick={voice.replay}>
                      <Icon name="replay" /> Replay this part
                    </button>
                  </div>
                </>
              )}
            </>
          ) : tray === 'history' ? (
            <>
              <p>
                Every operation applies to both entire sides. Undo returns to
                the previous equation.
              </p>
              {progress.history.length ? (
                <ol className="history-list">
                  {progress.history.map((entry, i) => (
                    <li key={i}>
                      <strong>{formatEquation(entry.equation)}</strong>
                      <span>
                        {entry.operation.kind} {entry.operation.value} on both
                        sides
                      </span>
                    </li>
                  ))}
                  <li>
                    <strong>{formatEquation(progress.equation)}</strong>
                    <span>Current equation</span>
                  </li>
                </ol>
              ) : (
                <p className="note">
                  Your first move will appear here. Try an operation in Explore.
                </p>
              )}
            </>
          ) : (
            <>
              <label className="setting-row">
                <span>
                  <strong>Tutor speaking pace</strong>
                  <small>
                    Choose a pace that gives you time to follow the reasoning.
                  </small>
                </span>
                <select
                  aria-label="Tutor speaking pace"
                  value={voice.rate}
                  onChange={(e) => voice.setRate(Number(e.target.value))}
                >
                  <option value={0.75}>Slower · 0.75×</option>
                  <option value={0.9}>Steady · 0.9×</option>
                  <option value={1}>Original · 1×</option>
                  <option value={1.15}>Quicker · 1.15×</option>
                </select>
              </label>
              <label className="setting-row">
                <span>
                  <strong>Advance the explanation with the tutor</strong>
                  <small>
                    After each recorded part finishes, move to the next. You can
                    always pause or go back.
                  </small>
                </span>
                <input
                  type="checkbox"
                  checked={autoAdvance}
                  onChange={(e) => setAutoAdvance(e.target.checked)}
                />
              </label>
              <label className="setting-row">
                <span>
                  <strong>Speak tutor replies</strong>
                  <small>
                    Prepare and play a voice answer after your question. Text
                    remains available.
                  </small>
                </span>
                <input
                  type="checkbox"
                  checked={voiceReplies}
                  onChange={(e) => {
                    setVoiceReplies(e.target.checked);
                    if (!e.target.checked) interrupt();
                  }}
                />
              </label>
              <p className="note">
                The same tutor delivers prepared lessons and live answers.
                Lesson recordings are reused; transcripts and their playback
                position keep questions in context.
              </p>
              <p>
                Turn the microphone on once to speak anytime. It stays on across
                questions and activity views until you turn it off or leave this
                workshop. Speech pauses the tutor immediately. Questions longer
                than 30 seconds need a shorter retry; the mic stays on. Headphones help prevent
                the tutor’s voice from being picked up.
              </p>
              <p className="microcopy">
                Reduced motion follows your device setting. The tutor’s position
                still indicates the part being explained.
              </p>
            </>
          )}
        </div>
        {error && (
          <p className="feedback" role="alert">
            {error}
          </p>
        )}
        {voice.error && (
          <p className="feedback" role="status">
            {voice.error}
          </p>
        )}
        {(teaching || currentSegment) && (
          <div className="tray-footer">
            <button
              className="primary"
              onClick={() => {
                interrupt();
                setTrayOpen(false);
                voice.resumeLesson();
              }}
            >
              Continue explanation <Icon name="play" />
            </button>
            <button
              className="text-button"
              onClick={() => {
                interrupt();
                setTrayOpen(false);
              }}
            >
              Return quietly
            </button>
          </div>
        )}
      </dialog>
      {(error || status || (voice.error && !trayOpen)) && (
        <div
          className={`notice ${error ? 'error' : ''}`}
          role={error ? 'alert' : 'status'}
        >
          <Icon name={error ? 'book' : 'check'} />
          <span>{error || voice.error || status}</span>
          <button
            className="round"
            aria-label="Dismiss message"
            onClick={() => {
              setError('');
              setStatus('');
              voice.clearError();
            }}
          >
            <Icon name="close" />
          </button>
        </div>
      )}
      <LearningRecord
        session={session}
        open={recordOpen}
        onClose={() => setRecordOpen(false)}
      />
    </div>
  );
}
